import { Link } from "react-router-dom";
import { imageUrl, DOMAINS } from "@/lib/api";
import { MapPin, Sparkle, GpsFix, Robot, CheckCircle, Trash, PencilSimple, ArrowsMerge, Check, Star, PlayCircle } from "@phosphor-icons/react";
import ConfidenceBadge from "@/components/ConfidenceBadge";

const STATUS_DOT = {
  analyzing: "bg-blue-500 animate-pulse",
  analyzed: "bg-zinc-400",
  needs_review: "bg-amber-500",
  approved: "bg-emerald-500",
  published: "bg-[#F27A54]",
};

export default function PhotoCard({ photo, index = 0, onApprove, onDelete, onDomain, onEdit, onMerge, onFeature, selectable, selected, onToggleSelect }) {
  const loc = photo.location || {};
  const place = [loc.city, loc.country].filter(Boolean).join(", ");
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const manageable = onApprove || onDelete || onDomain || onEdit || onMerge;
  const canApprove = photo.status !== "approved" && photo.status !== "published";
  const handleClick = (e) => {
    if (selectable) { e.preventDefault(); e.stopPropagation(); onToggleSelect?.(photo.id); }
  };
  return (
    <Link
      to={`/photos/${photo.id}`}
      onClick={handleClick}
      data-testid={`photo-card-${photo.id}`}
      className={`group relative block overflow-hidden rounded-md border bg-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-md fade-up ${
        selected ? "border-[#F27A54] ring-2 ring-[#F27A54]" : "border-border"
      }`}
      style={{ animationDelay: `${Math.min(index * 30, 400)}ms` }}
    >
      {selectable && (
        <button
          type="button"
          data-testid={`select-photo-${photo.id}`}
          onClick={(e) => { stop(e); onToggleSelect?.(photo.id); }}
          className={`absolute right-2 top-2 z-20 grid h-6 w-6 place-items-center rounded-full border-2 transition-colors ${
            selected ? "border-[#F27A54] bg-[#F27A54] text-white" : "border-white bg-black/40 text-transparent hover:bg-black/60"
          }`}
        >
          <Check size={13} weight="bold" />
        </button>
      )}
      {onFeature && !selectable && (
        <button
          type="button"
          data-testid={`feature-photo-${photo.id}`}
          title={photo.editors_pick ? "Remove from Editor's Picks" : "Add to Editor's Picks"}
          onClick={(e) => { stop(e); onFeature(photo.id, !photo.editors_pick); }}
          className={`absolute left-2 top-10 z-20 grid h-7 w-7 place-items-center rounded-full transition-colors ${
            photo.editors_pick ? "bg-amber-500 text-white" : "bg-black/45 text-white/80 hover:bg-black/65"
          }`}
        >
          <Star size={14} weight={photo.editors_pick ? "fill" : "bold"} />
        </button>
      )}
      <div className="relative aspect-square overflow-hidden bg-zinc-100">
        <img
          src={imageUrl(photo.id, "display")}
          alt={photo.caption || photo.original_filename}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {photo.media_type === "video" && (
          <span data-testid={`video-badge-${photo.id}`} className="absolute inset-0 grid place-items-center">
            <PlayCircle size={44} weight="fill" className="text-white/90 drop-shadow-lg" />
          </span>
        )}
        <div className="absolute left-2 top-2 flex items-center gap-1.5 rounded-sm bg-black/60 px-2 py-1 backdrop-blur-sm">
          <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[photo.status] || "bg-zinc-400"}`} />
          <span className="overline text-[9px] text-white">{(photo.status || "").replace("_", " ")}</span>
        </div>
        {photo.is_duplicate && (
          <span className="absolute right-2 top-2 rounded-sm bg-red-600/90 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Dup</span>
        )}
        {photo.location_source && (
          <span
            title={photo.location_source === "gps" ? "Located via GPS" : "Located via AI (no GPS)"}
            className={`absolute ${photo.is_duplicate ? "right-10" : "right-2"} top-2 flex items-center gap-1 rounded-sm px-1.5 py-1 text-[9px] font-bold uppercase text-white ${
              photo.location_source === "gps" ? "bg-emerald-600/90" : "bg-purple-600/90"
            }`}
          >
            {photo.location_source === "gps" ? <GpsFix size={10} weight="fill" /> : <Robot size={10} weight="fill" />}
            {photo.location_source === "gps" ? "GPS" : "AI"}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 transition-opacity duration-200 group-hover:translate-y-0 group-hover:opacity-100">
          <p className="line-clamp-1 text-xs font-medium text-white">{photo.caption || "Awaiting analysis…"}</p>
          {place && (
            <p className="mt-0.5 flex items-center gap-1 text-[10px] text-white/80">
              <MapPin size={10} weight="fill" /> {place}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 p-2">
        <span className="flex items-center gap-1 truncate text-[11px] font-semibold text-zinc-700">
          {photo.primary_domain && <Sparkle size={11} weight="fill" className="text-[#F27A54]" />}
          {photo.primary_domain || "Unclassified"}
        </span>
        <ConfidenceBadge score={photo.overall_confidence} />
      </div>

      {manageable && (
        <div className="flex items-center gap-1 border-t border-border p-1.5" onClick={stop}>
          {onDomain && (
            <select
              data-testid={`quick-domain-${photo.id}`}
              value={photo.primary_domain || ""}
              onClick={stop}
              onChange={(e) => { stop(e); onDomain(photo.id, e.target.value); }}
              className="h-7 min-w-0 flex-1 rounded-sm border border-border bg-white px-1 text-[11px] font-medium text-zinc-700 outline-none focus:ring-1 focus:ring-[#F27A54]"
            >
              <option value="" disabled>Category…</option>
              {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          {onApprove && canApprove && (
            <button data-testid={`quick-approve-${photo.id}`} title="Approve"
              onClick={(e) => { stop(e); onApprove(photo.id); }}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-sm border border-emerald-200 text-emerald-600 hover:bg-emerald-50">
              <CheckCircle size={15} weight="bold" />
            </button>
          )}
          {onEdit && (
            <button data-testid={`quick-edit-${photo.id}`} title="Edit title, description & tags"
              onClick={(e) => { stop(e); onEdit(photo); }}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-sm border border-border text-[#F27A54] hover:bg-blue-50">
              <PencilSimple size={15} weight="bold" />
            </button>
          )}
          {onMerge && photo.is_duplicate && (
            <button data-testid={`quick-merge-${photo.id}`} title="Merge into original & delete"
              onClick={(e) => { stop(e); onMerge(photo); }}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-sm border border-amber-300 text-amber-600 hover:bg-amber-50">
              <ArrowsMerge size={15} weight="bold" />
            </button>
          )}
          {onDelete && (
            <button data-testid={`quick-delete-${photo.id}`} title={photo.is_duplicate ? "Delete duplicate" : "Delete"}
              onClick={(e) => { stop(e); onDelete(photo.id, photo.is_duplicate); }}
              className={`grid h-7 w-7 shrink-0 place-items-center rounded-sm border hover:bg-red-50 ${photo.is_duplicate ? "border-red-300 text-red-600" : "border-border text-zinc-500"}`}>
              <Trash size={15} weight="bold" />
            </button>
          )}
        </div>
      )}
    </Link>
  );
}
